/**
 * Turn Platform Design System
 *
 * Warm light theme (parent portal) with dark admin override.
 * Primary accent: #5C2D91 (Turn Purple)
 * Background: #FAF6F0 (warm cream)
 *
 * Usage:
 *   import { colors, glassStyles, getStatusClasses } from '@/lib/design';
 */

import type { CSSProperties } from 'react';

// =============================================================================
// COLOR TOKENS
// =============================================================================

export const colors = {
  brand: {
    primary: '#5C2D91',
    primaryHover: '#4A2476',
    secondary: '#007AFF',
    accent: '#5C2D91',
  },

  primary: {
    50: '#F3EDF9',
    100: '#E1D0F0',
    200: '#C5A3E0',
    300: '#A876D0',
    400: '#7B49B8',
    500: '#5C2D91',
    600: '#4A2476',
    700: '#381B5B',
    800: '#261240',
    900: '#140925',
  },

  accent: {
    blue: '#007AFF',
    green: '#34C759',
    red: '#FF3B30',
    orange: '#FF9500',
    yellow: '#FFD60A',
    teal: '#5AC8FA',
    indigo: '#5856D6',
    purple: '#5C2D91',
    pink: '#FF2D55',
  },

  status: {
    success: '#34C759',
    successBg: 'rgba(52, 199, 89, 0.12)',
    warning: '#FF9500',
    warningBg: 'rgba(255, 149, 0, 0.12)',
    error: '#FF3B30',
    errorBg: 'rgba(255, 59, 48, 0.12)',
    info: '#5C2D91',
    infoBg: 'rgba(92, 45, 145, 0.08)',
  },

  severity: {
    critical: '#FF3B30',
    high: '#FF9500',
    medium: '#FFD60A',
    low: '#34C759',
    info: '#5C2D91',
  },

  neutral: {
    0: '#FFFFFF',
    50: '#FAF6F0',
    100: '#F5F1EB',
    200: '#EDE8E0',
    300: '#D6D0C8',
    400: '#B8B2AA',
    500: '#8E8E93',
    600: '#636366',
    700: '#48484A',
    800: '#2C2C2E',
    900: '#1C1C1E',
  },

  glass: {
    white80: 'rgba(255, 255, 255, 0.80)',
    white72: 'rgba(255, 255, 255, 0.72)',
    white65: 'rgba(255, 255, 255, 0.65)',
    white60: 'rgba(255, 255, 255, 0.60)',
    white40: 'rgba(255, 255, 255, 0.40)',
    stroke: 'rgba(255, 255, 255, 0.75)',
    shadow: 'rgba(92, 45, 145, 0.05)',
  },

  text: {
    primary: '#1C1C1E',
    secondary: '#636366',
    tertiary: '#8E8E93',
    disabled: '#C7C7CC',
    inverse: '#FFFFFF',
  },
} as const;

// =============================================================================
// GLASS EFFECT STYLES — Light Theme (Parent Portal)
// =============================================================================

export const glassStyles = {
  /** Tier 1: Primary glass card */
  card: {
    background: 'rgba(255, 255, 255, 0.60)',
    backdropFilter: 'saturate(180%) blur(20px)',
    WebkitBackdropFilter: 'saturate(180%) blur(20px)',
    border: '1px solid rgba(255, 255, 255, 0.75)',
    boxShadow: '0 8px 32px rgba(92, 45, 145, 0.05), 0 1px 3px rgba(0, 0, 0, 0.04), inset 0 1px 0 rgba(255, 255, 255, 0.85)',
    borderRadius: '16px',
  } as CSSProperties,

  /** Tier 2: Elevated glass card */
  cardElevated: {
    background: 'rgba(255, 255, 255, 0.72)',
    backdropFilter: 'saturate(200%) blur(28px) brightness(1.02)',
    WebkitBackdropFilter: 'saturate(200%) blur(28px) brightness(1.02)',
    border: '1px solid rgba(255, 255, 255, 0.85)',
    boxShadow: '0 16px 48px rgba(92, 45, 145, 0.08), 0 4px 12px rgba(0, 0, 0, 0.05), inset 0 1px 0 rgba(255, 255, 255, 0.95)',
    borderRadius: '20px',
  } as CSSProperties,

  /** Tier 3: Subtle glass card */
  cardSubtle: {
    background: 'rgba(255, 255, 255, 0.40)',
    backdropFilter: 'saturate(150%) blur(16px)',
    WebkitBackdropFilter: 'saturate(150%) blur(16px)',
    border: '1px solid rgba(255, 255, 255, 0.50)',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.03)',
    borderRadius: '12px',
  } as CSSProperties,

  /** Tier 4: Inset (inputs, text areas) */
  inset: {
    background: 'rgba(0, 0, 0, 0.03)',
    border: '1px solid rgba(0, 0, 0, 0.08)',
    boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.04)',
    borderRadius: '12px',
  } as CSSProperties,

  /** Tier 5: Header / Nav bar */
  header: {
    background: 'rgba(250, 246, 240, 0.85)',
    backdropFilter: 'saturate(180%) blur(32px)',
    WebkitBackdropFilter: 'saturate(180%) blur(32px)',
    borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
  } as CSSProperties,

  /** Tier 5 alias: Sidebar */
  sidebar: {
    background: 'rgba(250, 246, 240, 0.92)',
    backdropFilter: 'saturate(180%) blur(32px)',
    WebkitBackdropFilter: 'saturate(180%) blur(32px)',
    borderRight: '1px solid rgba(0, 0, 0, 0.06)',
    boxShadow: '1px 0 3px rgba(0, 0, 0, 0.04)',
  } as CSSProperties,

  /** Tier 6: Modal / Sheet */
  modal: {
    background: 'rgba(255, 255, 255, 0.80)',
    backdropFilter: 'saturate(200%) blur(40px) brightness(1.03)',
    WebkitBackdropFilter: 'saturate(200%) blur(40px) brightness(1.03)',
    border: '1px solid rgba(255, 255, 255, 0.90)',
    boxShadow: '0 24px 80px rgba(92, 45, 145, 0.12), 0 8px 24px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 1.0)',
    borderRadius: '24px',
  } as CSSProperties,

  /** Tier 7: Purple accent glass */
  accent: {
    background: 'rgba(92, 45, 145, 0.06)',
    backdropFilter: 'saturate(180%) blur(20px)',
    WebkitBackdropFilter: 'saturate(180%) blur(20px)',
    border: '1px solid rgba(92, 45, 145, 0.12)',
    boxShadow: '0 8px 32px rgba(92, 45, 145, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.60)',
    borderRadius: '16px',
  } as CSSProperties,

  /** Input field */
  input: {
    background: 'rgba(0, 0, 0, 0.03)',
    border: '1px solid rgba(0, 0, 0, 0.08)',
    boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.04)',
    borderRadius: '12px',
    color: '#1C1C1E',
  } as CSSProperties,

  /** Primary button */
  buttonPrimary: {
    background: '#5C2D91',
    boxShadow: '0 4px 16px rgba(92, 45, 145, 0.30)',
    border: 'none',
    color: '#FFFFFF',
    borderRadius: '12px',
  } as CSSProperties,

  /** Secondary button (glass) */
  buttonSecondary: {
    background: 'rgba(255, 255, 255, 0.60)',
    backdropFilter: 'saturate(180%) blur(16px)',
    WebkitBackdropFilter: 'saturate(180%) blur(16px)',
    border: '1px solid rgba(0, 0, 0, 0.08)',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
    color: '#1C1C1E',
    borderRadius: '12px',
  } as CSSProperties,

  /** Ghost button */
  buttonGhost: {
    background: 'transparent',
    border: 'none',
    color: '#5C2D91',
    borderRadius: '12px',
  } as CSSProperties,

  /** Danger button */
  buttonDanger: {
    background: '#FF3B30',
    boxShadow: '0 4px 16px rgba(255, 59, 48, 0.30)',
    border: 'none',
    color: '#FFFFFF',
    borderRadius: '12px',
  } as CSSProperties,

  /** Overlay */
  overlay: {
    background: 'rgba(0, 0, 0, 0.30)',
    backdropFilter: 'blur(8px)',
    WebkitBackdropFilter: 'blur(8px)',
  } as CSSProperties,

  /** Bottom sheet */
  bottomSheet: {
    background: 'rgba(255, 255, 255, 0.92)',
    backdropFilter: 'saturate(200%) blur(40px)',
    WebkitBackdropFilter: 'saturate(200%) blur(40px)',
    borderTop: '1px solid rgba(0, 0, 0, 0.06)',
    borderRadius: '24px 24px 0 0',
    boxShadow: '0 -8px 32px rgba(92, 45, 145, 0.08)',
  } as CSSProperties,
} as const;

// =============================================================================
// ADMIN (DARK) GLASS STYLES — used only inside .theme-admin
// =============================================================================

export const adminGlassStyles = {
  card: {
    background: 'rgba(255, 255, 255, 0.05)',
    backdropFilter: 'blur(40px) saturate(1.8)',
    WebkitBackdropFilter: 'blur(40px) saturate(1.8)',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.40)',
    borderRadius: '16px',
  } as CSSProperties,
  cardElevated: {
    background: 'rgba(255, 255, 255, 0.08)',
    backdropFilter: 'blur(48px) saturate(2)',
    WebkitBackdropFilter: 'blur(48px) saturate(2)',
    border: '1px solid rgba(255, 255, 255, 0.12)',
    boxShadow: '0 16px 48px rgba(0, 0, 0, 0.50)',
    borderRadius: '20px',
  } as CSSProperties,
  cardSubtle: {
    background: 'rgba(255, 255, 255, 0.03)',
    backdropFilter: 'blur(24px) saturate(1.5)',
    WebkitBackdropFilter: 'blur(24px) saturate(1.5)',
    border: '1px solid rgba(255, 255, 255, 0.05)',
    boxShadow: '0 4px 24px rgba(0, 0, 0, 0.30)',
    borderRadius: '12px',
  } as CSSProperties,
  header: {
    background: 'rgba(0, 0, 0, 0.80)',
    backdropFilter: 'blur(48px) saturate(2)',
    WebkitBackdropFilter: 'blur(48px) saturate(2)',
    borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
    boxShadow: '0 4px 24px rgba(0, 0, 0, 0.40)',
  } as CSSProperties,
  sidebar: {
    background: 'rgba(10, 10, 10, 0.95)',
    backdropFilter: 'blur(48px) saturate(2)',
    WebkitBackdropFilter: 'blur(48px) saturate(2)',
    borderRight: '1px solid rgba(255, 255, 255, 0.08)',
    boxShadow: '4px 0 24px rgba(0, 0, 0, 0.40)',
  } as CSSProperties,
  modal: {
    background: 'rgba(28, 28, 30, 0.95)',
    backdropFilter: 'blur(48px) saturate(2)',
    WebkitBackdropFilter: 'blur(48px) saturate(2)',
    border: '1px solid rgba(255, 255, 255, 0.15)',
    boxShadow: '0 24px 80px rgba(0, 0, 0, 0.60)',
    borderRadius: '24px',
  } as CSSProperties,
  input: {
    background: 'rgba(255, 255, 255, 0.05)',
    backdropFilter: 'blur(16px)',
    WebkitBackdropFilter: 'blur(16px)',
    border: '1px solid rgba(255, 255, 255, 0.10)',
    borderRadius: '12px',
    color: '#FFFFFF',
  } as CSSProperties,
  buttonPrimary: {
    background: '#5C2D91',
    boxShadow: '0 4px 16px rgba(92, 45, 145, 0.40)',
    border: 'none',
    color: '#FFFFFF',
    borderRadius: '12px',
  } as CSSProperties,
  buttonSecondary: {
    background: 'rgba(255, 255, 255, 0.08)',
    backdropFilter: 'blur(16px)',
    WebkitBackdropFilter: 'blur(16px)',
    border: '1px solid rgba(255, 255, 255, 0.12)',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.20)',
    color: '#FFFFFF',
    borderRadius: '12px',
  } as CSSProperties,
  buttonGhost: {
    background: 'transparent',
    border: 'none',
    color: '#5C2D91',
    borderRadius: '12px',
  } as CSSProperties,
  buttonDanger: {
    background: '#FF3B30',
    boxShadow: '0 4px 16px rgba(255, 59, 48, 0.40)',
    border: 'none',
    color: '#FFFFFF',
    borderRadius: '12px',
  } as CSSProperties,
  overlay: {
    background: 'rgba(0, 0, 0, 0.60)',
    backdropFilter: 'blur(8px)',
    WebkitBackdropFilter: 'blur(8px)',
  } as CSSProperties,
  bottomSheet: {
    background: 'rgba(28, 28, 30, 0.98)',
    backdropFilter: 'blur(48px) saturate(2)',
    WebkitBackdropFilter: 'blur(48px) saturate(2)',
    borderTop: '1px solid rgba(255, 255, 255, 0.10)',
    borderRadius: '24px 24px 0 0',
    boxShadow: '0 -8px 32px rgba(0, 0, 0, 0.50)',
  } as CSSProperties,
} as const;

// =============================================================================
// SHADOWS
// =============================================================================

export const shadows = {
  sm: '0 2px 4px rgba(0, 0, 0, 0.04)',
  md: '0 4px 16px rgba(0, 0, 0, 0.06)',
  lg: '0 8px 32px rgba(92, 45, 145, 0.05)',
  xl: '0 16px 48px rgba(92, 45, 145, 0.08)',
  '2xl': '0 24px 80px rgba(92, 45, 145, 0.12)',
  glass: '0 8px 32px rgba(92, 45, 145, 0.05), 0 1px 3px rgba(0, 0, 0, 0.04)',
  glow: (color: string, opacity = 0.3) => `0 0 32px rgba(${color}, ${opacity})`,
  glowPurple: '0 0 32px rgba(92, 45, 145, 0.30)',
  glowGreen: '0 0 32px rgba(52, 199, 89, 0.30)',
  glowRed: '0 0 32px rgba(255, 59, 48, 0.30)',
  inset: 'inset 0 1px 0 rgba(255, 255, 255, 0.85)',
} as const;

// =============================================================================
// TYPOGRAPHY
// =============================================================================

export const typography = {
  fontFamily: {
    display: "'Roboto Slab', 'Rockwell', serif",
    body: "'Manrope', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    mono: "'Overpass Mono', 'SF Mono', 'JetBrains Mono', monospace",
    turn: "'Comic Sans MS', 'Comic Sans', 'Chalkboard SE', cursive",
  },
  fontSize: {
    xs: '0.75rem',
    sm: '0.875rem',
    base: '1rem',
    lg: '1.125rem',
    xl: '1.25rem',
    '2xl': '1.5rem',
    '3xl': '1.875rem',
    '4xl': '2.25rem',
    '5xl': '3rem',
  },
  fontWeight: {
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
  lineHeight: {
    tight: 1.2,
    normal: 1.5,
    relaxed: 1.625,
  },
  letterSpacing: {
    tight: '-0.02em',
    normal: '0',
    wide: '0.02em',
  },
} as const;

// =============================================================================
// GRADIENTS
// =============================================================================

export const gradients = {
  appBg: '#FAF6F0',
  appBgSubtle: 'linear-gradient(180deg, #FAF6F0 0%, #F5F1EB 100%)',
  appBgRadial: 'radial-gradient(ellipse at top, #FFFFFF 0%, #FAF6F0 100%)',
  adminBg: 'linear-gradient(180deg, #0A0A0A 0%, #000000 100%)',
  accentPurple: 'linear-gradient(180deg, #7B49B8 0%, #5C2D91 100%)',
  accentGreen: 'linear-gradient(180deg, #34C759 0%, #28A745 100%)',
  surfaceElevated: 'linear-gradient(180deg, rgba(255,255,255,0.72) 0%, rgba(255,255,255,0.60) 100%)',
  rainbow: 'linear-gradient(90deg, #FF3B30, #FF9500, #FFD60A, #34C759, #007AFF, #5C2D91)',
} as const;

// =============================================================================
// SPACING
// =============================================================================

export const spacing = {
  xs: '0.25rem',
  sm: '0.5rem',
  md: '1rem',
  lg: '1.5rem',
  xl: '2rem',
  '2xl': '3rem',
  '3xl': '4rem',
  '4xl': '6rem',
} as const;

// =============================================================================
// BORDER RADIUS
// =============================================================================

export const borderRadius = {
  sm: '8px',
  md: '12px',
  lg: '16px',
  xl: '20px',
  '2xl': '24px',
  full: '9999px',
} as const;

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

type StatusType = 'success' | 'warning' | 'error' | 'info';

export function getStatusClasses(status: StatusType): string {
  const map: Record<StatusType, string> = {
    success: 'bg-[#34C759]/12 text-[#34C759] border-[#34C759]/25',
    warning: 'bg-[#FF9500]/12 text-[#FF9500] border-[#FF9500]/25',
    error: 'bg-[#FF3B30]/12 text-[#FF3B30] border-[#FF3B30]/25',
    info: 'bg-[#5C2D91]/8 text-[#5C2D91] border-[#5C2D91]/20',
  };
  return `inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${map[status]}`;
}

type SeverityType = 'critical' | 'high' | 'medium' | 'low' | 'info';

export function getSeverityClasses(severity: SeverityType): string {
  const map: Record<SeverityType, string> = {
    critical: 'bg-[#FF3B30]/12 text-[#FF3B30] border-[#FF3B30]/25',
    high: 'bg-[#FF9500]/12 text-[#FF9500] border-[#FF9500]/25',
    medium: 'bg-[#FFD60A]/12 text-[#FFD60A] border-[#FFD60A]/25',
    low: 'bg-[#34C759]/12 text-[#34C759] border-[#34C759]/25',
    info: 'bg-[#5C2D91]/8 text-[#5C2D91] border-[#5C2D91]/20',
  };
  return `inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${map[severity]}`;
}

export function getStatusColor(status: StatusType): string {
  return colors.status[status];
}

export function getSeverityColor(severity: SeverityType): string {
  return colors.severity[severity];
}

// =============================================================================
// CSS CLASS MAPPINGS
// =============================================================================

export const cx = {
  card: 'glass rounded-2xl',
  cardElevated: 'glass-elevated rounded-[20px]',
  cardSubtle: 'glass-subtle rounded-xl',

  btnPrimary: 'px-6 py-3 bg-[#5C2D91] hover:bg-[#4A2476] active:scale-[0.98] text-white font-medium rounded-xl transition-all focus:ring-2 focus:ring-[#5C2D91]/40 focus:ring-offset-2 focus:ring-offset-[#FAF6F0]',
  btnSecondary: 'px-6 py-3 bg-white/60 hover:bg-white/75 text-[#1C1C1E] font-medium rounded-xl border border-black/8 transition-all focus:ring-2 focus:ring-[#5C2D91]/20 focus:ring-offset-2 focus:ring-offset-[#FAF6F0]',
  btnGhost: 'px-6 py-3 text-[#5C2D91] hover:bg-[#5C2D91]/5 font-medium rounded-xl transition-all',
  btnDanger: 'px-6 py-3 bg-[#FF3B30] hover:brightness-110 active:scale-[0.98] text-white font-medium rounded-xl transition-all focus:ring-2 focus:ring-[#FF3B30]/40 focus:ring-offset-2 focus:ring-offset-[#FAF6F0]',
  btnIcon: 'p-3 hover:bg-black/5 rounded-xl transition-all min-w-[44px] min-h-[44px] flex items-center justify-center text-[#636366] hover:text-[#1C1C1E]',

  input: 'w-full px-4 py-3 bg-black/[0.03] border border-black/[0.08] rounded-xl text-[#1C1C1E] placeholder-[#8E8E93] focus:border-[#5C2D91]/40 focus:ring-2 focus:ring-[#5C2D91]/15 transition-all',
  textarea: 'w-full px-4 py-3 bg-black/[0.03] border border-black/[0.08] rounded-xl text-[#1C1C1E] placeholder-[#8E8E93] focus:border-[#5C2D91]/40 focus:ring-2 focus:ring-[#5C2D91]/15 transition-all resize-none',

  pageContainer: 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8',
  sectionTitle: 'text-xl font-semibold text-[#1C1C1E]',
  sectionSubtitle: 'text-sm text-[#636366] mt-1',

  statusDot: (status: StatusType) => {
    const colorMap: Record<StatusType, string> = {
      success: 'bg-[#34C759]',
      warning: 'bg-[#FF9500]',
      error: 'bg-[#FF3B30]',
      info: 'bg-[#5C2D91]',
    };
    return `w-2 h-2 rounded-full ${colorMap[status]}`;
  },

  textPrimary: 'text-[#1C1C1E]',
  textSecondary: 'text-[#636366]',
  textTertiary: 'text-[#8E8E93]',
  textDisabled: 'text-[#C7C7CC]',

  bgBase: 'bg-[#FAF6F0]',
  bgElevated1: 'bg-white',
  bgElevated2: 'bg-[#F5F1EB]',
  bgElevated3: 'bg-[#EDE8E0]',
  bgElevated4: 'bg-[#D6D0C8]',
} as const;

// =============================================================================
// ANIMATION TOKENS
// =============================================================================

export const animation = {
  duration: {
    fast: '150ms',
    normal: '200ms',
    slow: '300ms',
    slower: '500ms',
  },
  easing: {
    default: 'cubic-bezier(0.4, 0, 0.2, 1)',
    in: 'cubic-bezier(0.4, 0, 1, 1)',
    out: 'cubic-bezier(0, 0, 0.2, 1)',
    inOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
    spring: 'cubic-bezier(0.175, 0.885, 0.32, 1.275)',
  },
} as const;
